import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MESSAGE_STATUSES, type MessageRow, type MessageStatus } from '@mahallu/shared';
import { ApiError, loadErrorKey, messages, useMessageTemplates, useMessages } from '../api';
import { Badge, PageHeader } from '../components/Parts';
import { dateTime } from '../format';

const errorText = (e: unknown) => (e instanceof ApiError ? e.message : String(e));
const TONE: Record<MessageStatus, 'ok' | 'bad' | 'warn' | 'info' | 'neutral'> = { QUEUED: 'neutral', SENDING: 'info', SENT: 'info', DELIVERED: 'ok', READ: 'ok', FAILED: 'bad' };

function Row({ m }: { m: MessageRow }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const retry = useMutation({
    mutationFn: () => messages.retry(m.id, m.version),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['messages'] }),
  });
  return (
    <>
      <tr>
        <td className="num">{dateTime(m.createdAt, i18n.language)}</td>
        <td>
          {t(`template.${m.templateCode}`)}
          {m.subject.label ? <div className="note num">{m.subject.label}</div> : null}
        </td>
        <td className="num">{m.toMasked}</td>
        <td>{m.channel ? t(`channel.${m.channel}`) : t('channel.pending')}</td>
        <td>
          <Badge tone={TONE[m.status]}>{t(`mstatus2.${m.status}`)}</Badge>
          {m.lastError && m.status !== 'READ' && m.status !== 'DELIVERED' ? <div className="note">{m.lastError}</div> : null}
        </td>
        <td className="row-actions">
          <button type="button" className="btn sm" aria-expanded={open} onClick={() => setOpen(!open)}>
            {open ? t('delivery.hide') : t('delivery.steps', { n: m.events.length })}
          </button>
          {m.status === 'FAILED' ? (
            <button type="button" className="btn sm primary" disabled={retry.isPending} onClick={() => retry.mutate()}>
              {t('delivery.retry')}
            </button>
          ) : null}
          {retry.isError ? <span className="form-error">{errorText(retry.error)}</span> : null}
        </td>
      </tr>
      {open ? (
        <tr className="muted-row">
          <td colSpan={6}>
            <ul className="history">
              {m.events.map((e, i) => (
                <li key={i}>
                  <span className="num">{dateTime(e.at, i18n.language)}</span> · <b>{t(`mevent.${e.event}`)}</b>
                  {e.channel ? ` · ${t(`channel.${e.channel}`)}` : ''}
                  {e.detail ? <div className="note">{e.detail}</div> : null}
                </li>
              ))}
            </ul>
          </td>
        </tr>
      ) : null}
    </>
  );
}

/** Communication › Delivery log (pilot slice 8): every message to families, how it went, and the templates. */
export function DeliveryLog() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<MessageStatus | ''>('');
  const [page, setPage] = useState(1);
  const list = useMessages({ status, page, size: 25 });
  const templates = useMessageTemplates();
  const d = list.data;
  const pages = d ? Math.max(1, Math.ceil(d.total / d.size)) : 1;
  return (
    <>
      <PageHeader icon="mega" title={t('delivery.title')} desc={t('delivery.desc')} />
      <div className="filters" role="group" aria-label={t('rcpt.status')}>
        <button type="button" className={`btn sm${status === '' ? ' primary' : ''}`} aria-pressed={status === ''} onClick={() => { setStatus(''); setPage(1); }}>
          {t('delivery.all')}
        </button>
        {MESSAGE_STATUSES.map((s) => (
          <button key={s} type="button" className={`btn sm${status === s ? ' primary' : ''}`} aria-pressed={status === s} onClick={() => { setStatus(s); setPage(1); }}>
            {t(`mstatus2.${s}`)} <span className="num">{d?.counts[s] ?? 0}</span>
          </button>
        ))}
      </div>
      {list.isError ? <p className="form-error">{t(loadErrorKey(list.error))}</p> : null}
      <div className="tbl-wrap" style={{ marginTop: 12 }}>
        <div className="tbl-scroll">
          <table>
            <thead>
              <tr>
                <th>{t('delivery.when')}</th>
                <th>{t('delivery.what')}</th>
                <th>{t('delivery.to')}</th>
                <th>{t('delivery.channel')}</th>
                <th>{t('rcpt.status')}</th>
                <th>{t('col.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {(d?.rows ?? []).map((m) => (
                <Row key={m.id} m={m} />
              ))}
              {d && !d.rows.length ? (
                <tr>
                  <td colSpan={6} className="note">
                    {t('delivery.none')}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
      {pages > 1 ? (
        <div className="action-row">
          <button type="button" className="btn sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            {t('rcpt.prev')}
          </button>
          <span className="note">{t('rcpt.page', { page, pages })}</span>
          <button type="button" className="btn sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>
            {t('rcpt.next')}
          </button>
        </div>
      ) : null}
      <section className="card" style={{ marginTop: 16 }}>
        <div className="section-h">
          <h2>{t('delivery.templates')}</h2>
        </div>
        <p className="note">{t('delivery.templatesHint')}</p>
        <div className="tbl-wrap">
          <div className="tbl-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t('delivery.what')}</th>
                  <th>{t('delivery.channel')}</th>
                  <th>{t('delivery.lang')}</th>
                  <th>{t('rcpt.status')}</th>
                  <th>{t('delivery.wording')}</th>
                </tr>
              </thead>
              <tbody>
                {(templates.data ?? []).map((tp) => (
                  <tr key={`${tp.code}-${tp.channel}-${tp.lang}`}>
                    <td>{t(`template.${tp.code}`)}</td>
                    <td>{t(`channel.${tp.channel}`)}</td>
                    <td>{tp.lang === 'ML' ? 'മലയാളം' : 'English'}</td>
                    <td>
                      <Badge tone={tp.status === 'APPROVED' ? 'ok' : tp.status === 'REJECTED' ? 'bad' : 'warn'}>{t(`tstatus.${tp.status}`)}</Badge>
                    </td>
                    <td className={`note${tp.lang === 'ML' ? ' ml' : ''}`}>{tp.bodyPreview}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </>
  );
}
